import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, CardActions, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, Tooltip, Divider, LinearProgress, Table, TableHead,
  TableRow, TableCell, Paper,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import PersonIcon from '@mui/icons-material/Person';
import {
  MY_CEREMONIES_QUERY,
  MY_GUESTS_QUERY,
  MY_SEATING_ARRANGEMENTS_QUERY,
  CREATE_SEATING_ARRANGEMENT_MUTATION,
  UPDATE_SEATING_ARRANGEMENT_MUTATION,
  DELETE_SEATING_ARRANGEMENT_MUTATION,
  ASSIGN_SEAT_MUTATION,
  UPDATE_SEAT_ASSIGNMENT_MUTATION,
  REMOVE_SEAT_ASSIGNMENT_MUTATION,
} from '../graphql/queries';

const LAYOUT_LABELS = { dining: 'Dining', stage: 'Stage View', reception: 'Reception', open: 'Open Seating' };
const EMPTY_ARR = { ceremonyId: '', name: '', layoutType: 'dining', totalTables: 10, seatsPerTable: 8, notes: '' };
const EMPTY_SEAT = { tableNumber: 1, seatNumber: '', seatLabel: '', guestId: '', notes: '' };

export default function SeatingPage() {
  const [selectedArr, setSelectedArr] = useState(null);
  const [arrDialog, setArrDialog]     = useState(false);
  const [editArr, setEditArr]         = useState(null);
  const [arrForm, setArrForm]         = useState(EMPTY_ARR);
  const [seatDialog, setSeatDialog]   = useState(false);
  const [editSeat, setEditSeat]       = useState(null);
  const [seatForm, setSeatForm]       = useState(EMPTY_SEAT);
  const [filterCeremony, setFilter]   = useState('');
  const [error, setError]             = useState('');

  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: guestData }    = useQuery(MY_GUESTS_QUERY,    { fetchPolicy: 'cache-and-network' });
  const { data, loading, refetch } = useQuery(MY_SEATING_ARRANGEMENTS_QUERY, {
    variables: filterCeremony ? { ceremonyId: filterCeremony } : {},
    fetchPolicy: 'cache-and-network',
  });

  const ceremonies     = ceremonyData?.myCeremonies?.filter(c => c.status !== 'cancelled') || [];
  const guests         = guestData?.myGuests || [];
  const arrangements   = data?.mySeatingArrangements || [];
  const currentArr     = arrangements.find(a => a.id === selectedArr) || null;

  const [createArr] = useMutation(CREATE_SEATING_ARRANGEMENT_MUTATION, {
    onCompleted: () => { setArrDialog(false); setArrForm(EMPTY_ARR); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateArr] = useMutation(UPDATE_SEATING_ARRANGEMENT_MUTATION, {
    onCompleted: () => { setArrDialog(false); setEditArr(null); setArrForm(EMPTY_ARR); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteArr] = useMutation(DELETE_SEATING_ARRANGEMENT_MUTATION, {
    onCompleted: () => { setSelectedArr(null); refetch(); },
  });
  const [assignSeat]  = useMutation(ASSIGN_SEAT_MUTATION,  { onCompleted: () => { setSeatDialog(false); setSeatForm(EMPTY_SEAT); refetch(); setError(''); }, onError: e => setError(e.message) });
  const [updateSeat]  = useMutation(UPDATE_SEAT_ASSIGNMENT_MUTATION, { onCompleted: () => { setSeatDialog(false); setEditSeat(null); setSeatForm(EMPTY_SEAT); refetch(); setError(''); }, onError: e => setError(e.message) });
  const [removeSeat]  = useMutation(REMOVE_SEAT_ASSIGNMENT_MUTATION, { onCompleted: () => refetch() });

  const openCreateArr = () => { setEditArr(null); setArrForm({ ...EMPTY_ARR, ceremonyId: filterCeremony }); setArrDialog(true); };
  const openEditArr   = (a) => { setEditArr(a); setArrForm({ ceremonyId: a.ceremonyId, name: a.name, layoutType: a.layoutType, totalTables: a.totalTables, seatsPerTable: a.seatsPerTable, notes: a.notes || '' }); setArrDialog(true); };
  const openAddSeat   = () => { if (!currentArr) return; setEditSeat(null); setSeatForm({ ...EMPTY_SEAT, tableNumber: 1 }); setSeatDialog(true); };
  const openEditSeat  = (s) => { setEditSeat(s); setSeatForm({ tableNumber: s.tableNumber, seatNumber: s.seatNumber || '', seatLabel: s.seatLabel || '', guestId: s.guestId || '', notes: s.notes || '' }); setSeatDialog(true); };

  const handleSaveArr = () => {
    const vars = { ...arrForm, totalTables: Number(arrForm.totalTables), seatsPerTable: Number(arrForm.seatsPerTable) };
    if (editArr) updateArr({ variables: { id: editArr.id, ...vars } });
    else createArr({ variables: vars });
  };
  const handleSaveSeat = () => {
    const vars = { ...seatForm, tableNumber: Number(seatForm.tableNumber), seatNumber: seatForm.seatNumber ? Number(seatForm.seatNumber) : null, guestId: seatForm.guestId || null };
    if (editSeat) updateSeat({ variables: { id: editSeat.id, ...vars } });
    else assignSeat({ variables: { arrangementId: currentArr.id, ...vars } });
  };

  // Group seat assignments by table
  const seatsByTable = (currentArr?.seats || []).reduce((acc, seat) => {
    const t = seat.tableNumber;
    if (!acc[t]) acc[t] = [];
    acc[t].push(seat);
    return acc;
  }, {});

  const assignedGuestIds = (currentArr?.seats || []).map(s => s.guestId).filter(Boolean);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={1} alignItems="center">
          <EventSeatIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>Seating Arrangements</Typography>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateArr}>New Arrangement</Button>
      </Stack>

      <Stack direction="row" spacing={2} mb={3}>
        <TextField select label="Filter by Ceremony" size="small" value={filterCeremony} onChange={e => { setFilter(e.target.value); setSelectedArr(null); }} sx={{ minWidth: 200 }}>
          <MenuItem value="">All Ceremonies</MenuItem>
          {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Left: Arrangement list */}
        <Grid item xs={12} md={4}>
          {arrangements.length === 0 && !loading && (
            <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <EventSeatIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" variant="body2">No seating arrangements yet.</Typography>
            </Card>
          )}
          <Stack spacing={1}>
            {arrangements.map(arr => {
              const ceremony = ceremonies.find(c => c.id === arr.ceremonyId);
              const assignedCount = (arr.seats || []).length;
              return (
                <Card key={arr.id} variant="outlined" sx={{ cursor: 'pointer', borderColor: selectedArr === arr.id ? 'primary.main' : 'divider', bgcolor: selectedArr === arr.id ? 'primary.50' : 'background.paper' }} onClick={() => setSelectedArr(arr.id)}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography fontWeight={700}>{arr.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{ceremony?.name}</Typography>
                      </Box>
                      <Chip label={LAYOUT_LABELS[arr.layoutType] || arr.layoutType} size="small" />
                    </Stack>
                    <Stack direction="row" spacing={2} mt={0.5}>
                      <Typography variant="caption"><TableRestaurantIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> {arr.totalTables} tables × {arr.seatsPerTable} seats</Typography>
                      <Typography variant="caption"><PersonIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> {assignedCount} assigned</Typography>
                    </Stack>
                  </CardContent>
                  <CardActions sx={{ pt: 0 }}>
                    <IconButton size="small" onClick={e => { e.stopPropagation(); openEditArr(arr); }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); deleteArr({ variables: { id: arr.id } }); }}><DeleteIcon fontSize="small" /></IconButton>
                  </CardActions>
                </Card>
              );
            })}
          </Stack>
        </Grid>

        {/* Right: Seating grid */}
        <Grid item xs={12} md={8}>
          {!currentArr && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', pt: 8 }}>
              <TableRestaurantIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">Select an arrangement to manage seats</Typography>
            </Paper>
          )}
          {currentArr && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{currentArr.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentArr.totalTables} tables × {currentArr.seatsPerTable} seats = <strong>{currentArr.totalTables * currentArr.seatsPerTable}</strong> capacity
                    &nbsp;|&nbsp; <strong>{(currentArr.seats || []).length}</strong> assigned
                  </Typography>
                </Box>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAddSeat}>Assign Seat</Button>
              </Stack>
              {Object.keys(seatsByTable).length === 0 && (
                <Alert severity="info">No seat assignments yet. Click "Assign Seat" to place guests at tables.</Alert>
              )}
              {Array.from({ length: currentArr.totalTables }, (_, i) => i + 1).map(tableNum => {
                const tableSeats = seatsByTable[tableNum] || [];
                if (tableSeats.length === 0) return null;
                return (
                  <Card key={tableNum} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <TableRestaurantIcon color="action" fontSize="small" />
                        <Typography fontWeight={700}>Table {tableNum}</Typography>
                        <Chip label={`${tableSeats.length}/${currentArr.seatsPerTable}`} size="small" color={tableSeats.length >= currentArr.seatsPerTable ? 'success' : 'default'} />
                      </Stack>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Seat</TableCell>
                            <TableCell>Guest</TableCell>
                            <TableCell>Label</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tableSeats.map(seat => (
                            <TableRow key={seat.id}>
                              <TableCell>{seat.seatNumber || '—'}</TableCell>
                              <TableCell>
                                {seat.guest ? (
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Typography variant="body2">{seat.guest.name}</Typography>
                                    {seat.guest.isVip && <Chip label="VIP" size="small" color="warning" />}
                                  </Stack>
                                ) : <Typography variant="body2" color="text.secondary">Unassigned</Typography>}
                              </TableCell>
                              <TableCell>{seat.seatLabel || '—'}</TableCell>
                              <TableCell align="right">
                                <IconButton size="small" onClick={() => openEditSeat(seat)}><EditIcon fontSize="small" /></IconButton>
                                <IconButton size="small" color="error" onClick={() => removeSeat({ variables: { id: seat.id } })}><DeleteIcon fontSize="small" /></IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                );
              })}
              {/* Show tables with no assignments as summary */}
              {currentArr.totalTables > Object.keys(seatsByTable).filter(k => (seatsByTable[k] || []).length > 0).length && (
                <Alert severity="info">
                  {currentArr.totalTables - Object.keys(seatsByTable).filter(k => (seatsByTable[k] || []).length > 0).length} tables have no seat assignments yet.
                </Alert>
              )}
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Create/Edit Arrangement Dialog */}
      <Dialog open={arrDialog} onClose={() => setArrDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editArr ? 'Edit Arrangement' : 'New Seating Arrangement'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select required label="Ceremony" fullWidth value={arrForm.ceremonyId} onChange={e => setArrForm(f => ({ ...f, ceremonyId: e.target.value }))}>
              {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField required label="Name" fullWidth placeholder="e.g. Main Dining Hall" value={arrForm.name} onChange={e => setArrForm(f => ({ ...f, name: e.target.value }))} />
            <TextField select label="Layout Type" fullWidth value={arrForm.layoutType} onChange={e => setArrForm(f => ({ ...f, layoutType: e.target.value }))}>
              {Object.entries(LAYOUT_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField required label="Total Tables" type="number" fullWidth value={arrForm.totalTables} onChange={e => setArrForm(f => ({ ...f, totalTables: e.target.value }))} inputProps={{ min: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <TextField required label="Seats per Table" type="number" fullWidth value={arrForm.seatsPerTable} onChange={e => setArrForm(f => ({ ...f, seatsPerTable: e.target.value }))} inputProps={{ min: 1 }} />
              </Grid>
            </Grid>
            <Typography variant="body2" color="primary.main" fontWeight={600}>
              Total Capacity: {Number(arrForm.totalTables || 0) * Number(arrForm.seatsPerTable || 0)} seats
            </Typography>
            <TextField label="Notes" fullWidth multiline rows={2} value={arrForm.notes} onChange={e => setArrForm(f => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArrDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveArr} disabled={!arrForm.ceremonyId || !arrForm.name}>{editArr ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Seat Dialog */}
      <Dialog open={seatDialog} onClose={() => setSeatDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editSeat ? 'Edit Seat' : 'Assign Seat'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="Guest (optional)" fullWidth value={seatForm.guestId} onChange={e => setSeatForm(f => ({ ...f, guestId: e.target.value }))}>
              <MenuItem value="">Unassigned</MenuItem>
              {guests.filter(g => !assignedGuestIds.includes(g.id) || g.id === seatForm.guestId).map(g => (
                <MenuItem key={g.id} value={g.id}>{g.name}{g.isVip ? ' (VIP)' : ''}</MenuItem>
              ))}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField required label="Table #" type="number" fullWidth value={seatForm.tableNumber} onChange={e => setSeatForm(f => ({ ...f, tableNumber: e.target.value }))} inputProps={{ min: 1, max: currentArr?.totalTables }} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Seat #" type="number" fullWidth value={seatForm.seatNumber} onChange={e => setSeatForm(f => ({ ...f, seatNumber: e.target.value }))} />
              </Grid>
            </Grid>
            <TextField label="Seat Label" fullWidth placeholder="e.g. VIP Table A - Seat 1" value={seatForm.seatLabel} onChange={e => setSeatForm(f => ({ ...f, seatLabel: e.target.value }))} />
            <TextField label="Notes" fullWidth value={seatForm.notes} onChange={e => setSeatForm(f => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeatDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSeat}>{editSeat ? 'Save' : 'Assign'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
